import { useTranslation } from "react-i18next";

// Single source of truth for the gate threshold: an analytics scope is gated
// when it spans more than one active rubric. The pages use this to decide
// whether to render <RubricGate/> in place of their per-standard sections, and
// the component itself uses it to self-hide — so the threshold lives in one
// place.
export const isRubricGated = (activeRubrics) =>
  (activeRubrics?.count || 0) > 1;

// Shown on the analytics pages when the current scope spans more than one active
// rubric — a head org whose child schools run different observation rubrics.
// Per-standard analytics can't be combined across rubrics, so instead of
// fabricating cross-rubric numbers we direct the viewer to the school selector
// already on the page to drill into a single school (which re-resolves the scope
// to one rubric). Renders nothing when the scope already resolves to a single
// rubric, so callers can drop it in unconditionally.
export default function RubricGate({ activeRubrics, className = "" }) {
  const { t } = useTranslation();
  if (!isRubricGated(activeRubrics)) return null;
  const count = activeRubrics?.count || 0;

  return (
    <div
      className={`bg-white rounded-2xl border border-slate-200 p-10 text-center ${className}`}
    >
      <div className="mx-auto w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4">
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="1.8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 17v-6h13M9 17H4a1 1 0 01-1-1V5a1 1 0 011-1h5m0 13l-3-3m3 3l-3 3M22 5H9m13 6H9"
          />
        </svg>
      </div>
      <h3 className="text-base font-bold text-slate-800">
        {t("analytics.multiRubricTitle")}
      </h3>
      <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto leading-relaxed">
        {t("analytics.multiRubricBody", { count })}
      </p>
    </div>
  );
}
